import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogBody,
  Button,
  Spinner,
  RadioGroup,
  Radio,
  Badge,
} from '@fluentui/react-components';
import {
  PersonRegular,
  PersonAddRegular,
  MailRegular,
  CheckmarkCircleRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { searchXeroContacts, resolveXeroContactId } from '../services/api';
import type { XeroContact } from '../types';

interface XeroContactVerificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientName: string;
  clientEmail?: string;
  taxReturnId?: string;
  /** When billing from a specific Schedule C business, resolve that business's Xero contact. */
  scheduleCBusinessId?: string;
  onConfirm: (xeroContactId: string) => void;
}

export default function XeroContactVerificationDialog({
  open,
  onOpenChange,
  clientName,
  clientEmail,
  taxReturnId,
  scheduleCBusinessId,
  onConfirm,
}: XeroContactVerificationDialogProps) {
  const [contacts, setContacts] = useState<XeroContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string>('__new__');
  const [resolvedFromDataverse, setResolvedFromDataverse] = useState(false);

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setError(null);
    setContacts([]);
    setSelectedContactId('__new__');
    setResolvedFromDataverse(false);

    // Run both calls in parallel: resolve from Dataverse + search Xero by name
    const resolvePromise = taxReturnId
      ? resolveXeroContactId(taxReturnId, undefined, scheduleCBusinessId).catch(() => ({ xeroContactId: null, resolvedFrom: null }))
      : Promise.resolve({ xeroContactId: null, resolvedFrom: null });

    const searchPromise = searchXeroContacts(clientName).catch(() => [] as XeroContact[]);

    Promise.all([resolvePromise, searchPromise])
      .then(([resolved, results]) => {
        setContacts(results);

        // If Dataverse has a XeroContactId, pre-select the matching contact
        if (resolved.xeroContactId) {
          const dataverseMatch = results.find(
            (c) => c.contactId === resolved.xeroContactId
          );
          if (dataverseMatch) {
            setSelectedContactId(dataverseMatch.contactId);
            setResolvedFromDataverse(true);
            return;
          }
          // Contact ID exists in Dataverse but wasn't in search results - still use it
          setSelectedContactId(resolved.xeroContactId);
          setResolvedFromDataverse(true);
          return;
        }

        // Fall back to exact name match
        const exactMatch = results.find(
          (c) => c.name.toLowerCase() === clientName.toLowerCase()
        );
        if (exactMatch) {
          setSelectedContactId(exactMatch.contactId);
        } else if (results.length === 0) {
          setSelectedContactId('__new__');
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to search contacts');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, clientName, taxReturnId, scheduleCBusinessId]);

  const handleConfirm = () => {
    // Send "__create_new__" sentinel so the backend knows to force-create a new Xero contact
    // instead of falling back to Dataverse lookup or name matching
    const contactId = selectedContactId === '__new__' ? '__create_new__' : selectedContactId;
    onConfirm(contactId);
  };

  const selectedContact = contacts.find((c) => c.contactId === selectedContactId);
  const isExactMatch = selectedContact?.name.toLowerCase() === clientName.toLowerCase();

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface style={{ maxWidth: '520px' }}>
        <DialogBody>
          <DialogTitle>Verify Xero Contact</DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', marginBottom: '4px' }}>
                Creating invoice for:
              </div>
              <div style={{ fontWeight: 600, fontSize: '15px' }}>{clientName}</div>
              {clientEmail && (
                <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{clientEmail}</div>
              )}
            </div>

            <div style={{
              padding: '10px 12px',
              backgroundColor: '#fff4ce',
              borderRadius: '6px',
              border: '1px solid #f0c800',
              marginBottom: '16px',
              fontSize: '13px',
            }}>
              <WarningRegular style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Please confirm the correct Xero contact to avoid creating the invoice for the wrong person.
            </div>

            {loading && (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <Spinner size="medium" label="Searching Xero contacts..." />
              </div>
            )}

            {error && (
              <div style={{ color: '#d83b01', fontSize: '13px', padding: '12px 0' }}>
                <WarningRegular style={{ marginRight: '4px' }} />
                {error}
              </div>
            )}

            {!loading && !error && (
              <RadioGroup
                value={selectedContactId}
                onChange={(_, data) => setSelectedContactId(data.value)}
              >
                {contacts.map((contact) => {
                  const nameMatch = contact.name.toLowerCase() === clientName.toLowerCase();
                  const isDataverseResolved = resolvedFromDataverse && selectedContactId === contact.contactId;
                  return (
                    <div
                      key={contact.contactId}
                      style={{
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: selectedContactId === contact.contactId
                          ? '1px solid #0078d4'
                          : '1px solid var(--tl-border)',
                        backgroundColor: selectedContactId === contact.contactId
                          ? '#f0f6ff'
                          : '#fff',
                        marginBottom: '8px',
                        cursor: 'pointer',
                      }}
                      onClick={() => setSelectedContactId(contact.contactId)}
                    >
                      <Radio
                        value={contact.contactId}
                        label={
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <PersonRegular />
                            <div>
                              <div style={{ fontWeight: 500 }}>
                                {contact.name}
                                {isDataverseResolved && (
                                  <Badge
                                    appearance="filled"
                                    color="success"
                                    size="small"
                                    style={{ marginLeft: '8px' }}
                                  >
                                    <CheckmarkCircleRegular style={{ marginRight: '2px', fontSize: '10px' }} />
                                    Linked in Dataverse
                                  </Badge>
                                )}
                                {nameMatch && !isDataverseResolved && (
                                  <Badge
                                    appearance="filled"
                                    color="success"
                                    size="small"
                                    style={{ marginLeft: '8px' }}
                                  >
                                    <CheckmarkCircleRegular style={{ marginRight: '2px', fontSize: '10px' }} />
                                    Exact match
                                  </Badge>
                                )}
                              </div>
                              {contact.email && (
                                <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <MailRegular style={{ fontSize: '12px' }} />
                                  {contact.email}
                                </div>
                              )}
                            </div>
                          </div>
                        }
                      />
                    </div>
                  );
                })}

                {/* Create new contact option */}
                <div
                  style={{
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: selectedContactId === '__new__'
                      ? '1px solid #0078d4'
                      : '1px solid var(--tl-border)',
                    backgroundColor: selectedContactId === '__new__'
                      ? '#f0f6ff'
                      : 'var(--tl-bg-page)',
                    marginBottom: '4px',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedContactId('__new__')}
                >
                  <Radio
                    value="__new__"
                    label={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <PersonAddRegular />
                        <div>
                          <div style={{ fontWeight: 500 }}>Create new contact</div>
                          <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>
                            None of the above match &mdash; create &quot;{clientName}&quot; as a new Xero contact
                          </div>
                        </div>
                      </div>
                    }
                  />
                </div>
              </RadioGroup>
            )}

            {!loading && !error && contacts.length === 0 && !resolvedFromDataverse && (
              <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', marginTop: '8px' }}>
                No existing contacts found. A new contact will be created in Xero.
              </div>
            )}

            {!loading && !error && resolvedFromDataverse && contacts.length === 0 && (
              <div style={{
                padding: '10px 12px',
                backgroundColor: '#dff6dd',
                borderRadius: '6px',
                border: '1px solid #107c10',
                fontSize: '13px',
                marginTop: '8px',
              }}>
                <CheckmarkCircleRegular style={{ marginRight: '6px', verticalAlign: 'middle', color: '#107c10' }} />
                Xero contact resolved from Dataverse. The linked contact will be used for this invoice.
              </div>
            )}
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleConfirm}
              disabled={loading}
            >
              {selectedContactId === '__new__'
                ? 'Create New Contact & Generate Invoice'
                : isExactMatch
                  ? 'Confirm & Generate Invoice'
                  : 'Use Selected Contact & Generate Invoice'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
